import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import type { ExerciseStatsRow } from '../../services/types';

type StatsSortKey = 'totalVolumeKg' | 'maxWeightKg' | 'sessionCount' | 'lastSessionDate';

interface ExerciseStatsTableProps {
  rows: ExerciseStatsRow[];
  loading?: boolean;
  title?: string;
}

const formatMetric = (value: number) =>
  value.toLocaleString('bg-BG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const formatSessionDate = (value: string | null) =>
  (value ? new Date(value).toLocaleDateString('bg-BG') : '-');

/**
 * Per-exercise comparison stats table (sortable). Used by the single-exercise/all
 * view and the exercise-group view.
 */
const ExerciseStatsTable: React.FC<ExerciseStatsTableProps> = ({
  rows,
  loading = false,
  title = 'Статистика по упражнения',
}) => {
  const [sortBy, setSortBy] = React.useState<StatsSortKey>('totalVolumeKg');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('desc');

  const sortedRows = React.useMemo(() => {
    return rows
      .map((row, index) => ({ row, index }))
      .sort((a, b) => {
        let comparison = 0;
        if (sortBy === 'lastSessionDate') {
          comparison = (a.row.lastSessionDate ?? '').localeCompare(b.row.lastSessionDate ?? '');
        } else {
          comparison = (a.row[sortBy] as number) - (b.row[sortBy] as number);
        }
        if (comparison === 0) return a.index - b.index;
        return sortDir === 'desc' ? -comparison : comparison;
      })
      .map(({ row }) => row);
  }, [rows, sortBy, sortDir]);

  const toggleSort = (key: StatsSortKey) => {
    if (sortBy === key) {
      setSortDir((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(key);
      setSortDir('desc');
    }
  };

  const sortArrow = (key: StatsSortKey) => (sortBy === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '');

  return (
    <Paper className="no-print" sx={{ p: { xs: 1, sm: 2 }, mb: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 1.5, fontWeight: 600 }}>{title}</Typography>

      {loading ? (
        <Typography sx={{ p: 1 }}>Зареждане...</Typography>
      ) : sortedRows.length === 0 ? (
        <Typography sx={{ p: 1 }}>Няма данни за избраните филтри</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #555' }}>
                <th style={{ padding: '5px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Упражнение</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button type="button" onClick={() => toggleSort('totalVolumeKg')} style={{ all: 'unset', cursor: 'pointer' }}>
                    Обем (kg){sortArrow('totalVolumeKg')}
                  </button>
                </th>
                <th style={{ padding: '5px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Ср. тежест</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button type="button" onClick={() => toggleSort('maxWeightKg')} style={{ all: 'unset', cursor: 'pointer' }}>
                    Макс тежест{sortArrow('maxWeightKg')}
                  </button>
                </th>
                <th style={{ padding: '5px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Серии</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Повт.</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                  <button type="button" onClick={() => toggleSort('sessionCount')} style={{ all: 'unset', cursor: 'pointer' }}>
                    Брой изпълнения{sortArrow('sessionCount')}
                  </button>
                </th>
                <th style={{ padding: '5px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>Ср. RPE</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>1RM</th>
                <th style={{ padding: '5px 8px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                  <button type="button" onClick={() => toggleSort('lastSessionDate')} style={{ all: 'unset', cursor: 'pointer' }}>
                    Последно изпълнение{sortArrow('lastSessionDate')}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => (
                <tr key={row.exerciseId} style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                  <td style={{ padding: '4px 8px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {row.exerciseName}{row.variationName ? ` (${row.variationName})` : ''}
                  </td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{formatMetric(row.totalVolumeKg)}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{formatMetric(row.avgWeightKg)}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{formatMetric(row.maxWeightKg)}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{row.totalSets}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{row.totalReps}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{row.sessionCount}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{formatMetric(row.avgRpe)}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{formatMetric(row.bestEstimated1RMKg)}</td>
                  <td style={{ padding: '4px 8px', whiteSpace: 'nowrap' }}>{formatSessionDate(row.lastSessionDate)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
    </Paper>
  );
};

export default ExerciseStatsTable;
